import React, { Component, ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('❌ Erro capturado pelo ErrorBoundary:', error);
    console.error('📍 Informações do erro:', errorInfo);
    
    this.setState({
      error,
      errorInfo: errorInfo.componentStack
    });
  }

  private isConfigurationError(error: Error): boolean {
    const configErrors = [
      'não configurada',
      'API key',
      'chave de API',
      'configure',
      'VITE_'
    ];
    
    return configErrors.some(keyword => 
      error.message.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const isConfigError = this.isConfigurationError(this.state.error);
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl bg-white/10 backdrop-blur border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {isConfigError ? (
                  <>
                    🔧 <span>Configuração Necessária</span>
                  </>
                ) : (
                  <>
                    ❌ <span>Erro na Aplicação</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-500/20 border border-red-300/30 rounded-lg p-4">
                <h3 className="text-red-200 font-medium mb-2">Erro:</h3>
                <p className="text-red-100 text-sm">{this.state.error.message}</p>
              </div>

              {isConfigError ? (
                <div className="bg-blue-500/20 border border-blue-300/30 rounded-lg p-4">
                  <h3 className="text-blue-200 font-medium mb-2">Como resolver:</h3>
                  <div className="text-blue-100 text-sm space-y-2">
                    <p>1. Crie um arquivo <code className="bg-black/30 px-1 rounded">.env</code> na raiz do projeto</p>
                    <p>2. Configure pelo menos uma API key:</p>
                    <div className="bg-black/30 p-2 rounded font-mono text-xs">
                      VITE_OPENAI_API_KEY=sk-sua-chave-openai<br/>
                      VITE_ANTHROPIC_API_KEY=sk-ant-sua-chave-anthropic<br/>
                      VITE_GOOGLE_API_KEY=sua-chave-google<br/>
                      VITE_PERPLEXITY_API_KEY=pplx-sua-chave-perplexity
                    </div>
                    <p>3. Reinicie o servidor: <code className="bg-black/30 px-1 rounded">npm run dev</code></p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-500/20 border border-gray-300/30 rounded-lg p-4">
                  <h3 className="text-gray-200 font-medium mb-2">Informações técnicas:</h3>
                  <pre className="text-gray-100 text-xs overflow-auto max-h-32">
                    {this.state.errorInfo}
                  </pre>
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  onClick={this.handleReload}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Recarregar Página
                </Button>
                <Button 
                  onClick={this.handleReset}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Tentar Novamente
                </Button>
              </div>

              <div className="text-xs text-gray-400 pt-2 border-t border-white/10">
                <p>Se o problema persistir, verifique o console do navegador para mais detalhes.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
